import type {
  CreateMyRepChatIntegrationPayload,
  MyRepChatIntegration,
  UpdateMyRepChatIntegrationPayload,
} from "@/schema/my-rep-chat-integration"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiContextFetch } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

export const MY_REP_CHAT_INTEGRATIONS_QUERY_KEY = ["my-rep-chat-integrations"]

export function useFetchMyRepChatIntegrations(enabled = true) {
  const getContext = useApiContext()
  const bookId = useBookId()

  return useQuery<MyRepChatIntegration[]>({
    queryKey: [...MY_REP_CHAT_INTEGRATIONS_QUERY_KEY, bookId],
    queryFn: async () => {
      const context = await getContext()
      const data = await apiContextFetch<MyRepChatIntegration[]>(context, "my_rep_chat_integrations")
      return data
    },
    enabled,
  })
}

export function useFetchMyRepChatIntegrationById(id: string | null) {
  const getContext = useApiContext()
  const bookId = useBookId()

  return useQuery<MyRepChatIntegration>({
    queryKey: ["my-rep-chat-integration", id, bookId],
    enabled: !!id,
    queryFn: async () => {
      const context = await getContext()
      if (!id) throw new Error("No id found")
      const data = await apiContextFetch<MyRepChatIntegration>(context, `my_rep_chat_integrations/${id}`)
      return data
    },
  })
}

export function useCreateMyRepChatIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  const bookId = useBookId()

  return useMutation({
    mutationFn: async (params: CreateMyRepChatIntegrationPayload) => {
      const context = await getContext()
      const data = await apiContextFetch<MyRepChatIntegration>(context, "my_rep_chat_integrations", {
        method: "POST",
        body: JSON.stringify({ my_rep_chat_integration: params }),
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...MY_REP_CHAT_INTEGRATIONS_QUERY_KEY, bookId] })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}

export function useUpdateMyRepChatIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  const bookId = useBookId()

  return useMutation({
    mutationFn: async ({ id, ...params }: UpdateMyRepChatIntegrationPayload & { id: string }) => {
      const context = await getContext()
      const data = await apiContextFetch<MyRepChatIntegration>(context, `my_rep_chat_integrations/${id}`, {
        method: "PUT",
        body: JSON.stringify({ my_rep_chat_integration: params }),
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...MY_REP_CHAT_INTEGRATIONS_QUERY_KEY, bookId] })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}

export function useDeleteMyRepChatIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  const bookId = useBookId()

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const context = await getContext()
      await apiContextFetch(context, `my_rep_chat_integrations/${id}`, {
        method: "DELETE",
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...MY_REP_CHAT_INTEGRATIONS_QUERY_KEY, bookId] })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}

export function useDisconnectMyRepChatIntegration() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  const bookId = useBookId()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      await apiContextFetch(context, `my_rep_chat_integrations/${id}`, {
        method: "PUT",
        body: JSON.stringify({ disconnect: true }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...MY_REP_CHAT_INTEGRATIONS_QUERY_KEY, bookId] })
      queryClient.invalidateQueries({ queryKey: ["sms-messages"] })
      queryClient.invalidateQueries({ queryKey: ["call-logs"] })
    },
  })
}
